import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CurrencyDollar, Download, CheckCircle, Warning, ArrowsClockwise, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** GUEST-VIEW invoice page — reachable by anyone who has the public_token in the URL.
 *  No login required. Emailed pay links land here. */
export default function PublicInvoicePay() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const paidFlag = search.get("paid") === "1";
  const cancelledFlag = search.get("cancelled") === "1";

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/public/invoices/${token}`);
      setInv(data);
    } catch (e) {
      setError(e.response?.status === 404 ? "This invoice link is invalid or has been voided." : "Couldn't load the invoice.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  // If we just landed back from Stripe with paid=1, keep polling for a few seconds so the
  // webhook has time to credit the invoice — then hard-refetch.
  useEffect(() => {
    if (!paidFlag) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/invoices/${token}`);
        if (!cancelled) setInv(data);
        if (data?.status !== "paid" && attempts < 6) {
          setTimeout(tick, 2000);
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    return () => { cancelled = true; };
  }, [paidFlag, token]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Shell><div className="text-[#a3a3a3]" data-testid="public-invoice-loading">Loading invoice…</div></Shell>
    );
  }

  if (error || !inv) {
    return (
      <Shell>
        <div className="border border-[#262626] bg-[#0a0a0a] p-8 text-center" data-testid="public-invoice-error">
          <Warning size={40} weight="duotone" className="text-[#FF2D2D] mx-auto mb-4" />
          <h1 className="font-display text-3xl uppercase">Link invalid</h1>
          <p className="text-[#a3a3a3] mt-2">{error || "Invoice not available."}</p>
          <p className="text-[11px] text-[#666] mt-4">If you believe this is a mistake, reply to the invoice email you received.</p>
        </div>
      </Shell>
    );
  }

  const total = Number(inv.total_usd || 0);
  const paid = Number(inv.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const isPaid = inv.status === "paid" || balance <= 0;
  const isPartial = inv.status === "partial" || (paid > 0 && !isPaid);

  return (
    <Shell>
      {paidFlag && !isPaid && (
        <div className="border border-[#FFD43B] bg-[#FFD43B]/5 p-4 mb-6 flex items-center gap-3" data-testid="pending-banner">
          <ArrowsClockwise size={20} weight="bold" className="text-[#FFD43B] animate-spin" />
          <div>
            <div className="text-sm text-white">Payment received — confirming with Stripe…</div>
            <div className="text-[11px] text-[#a3a3a3] mt-0.5">This usually takes a few seconds. Feel free to close this page — a receipt will be emailed.</div>
          </div>
        </div>
      )}
      {cancelledFlag && !isPaid && (
        <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-4 mb-6" data-testid="cancelled-banner">
          <div className="text-sm text-white">Payment cancelled. You can try again below whenever you&apos;re ready.</div>
        </div>
      )}

      <div className="max-w-2xl mx-auto" data-testid="public-invoice-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]" data-testid="public-inv-number">Invoice {inv.invoice_number}</div>
        <h1 className="font-display text-5xl mt-2">{inv.title}</h1>
        <div className="text-sm text-[#a3a3a3] mt-1">
          Billed to <span className="text-white">{inv.client_name}</span>
          {inv.client_company ? ` · ${inv.client_company}` : ""}
        </div>

        <div className={`mt-8 border p-6 ${isPaid ? "border-[#28C878] bg-gradient-to-b from-[#28C878]/10 to-transparent" : "border-[#FF2D2D] bg-gradient-to-b from-[#FF2D2D]/10 to-transparent"}`}>
          <div className={`text-[10px] uppercase tracking-[0.3em] ${isPaid ? "text-[#28C878]" : isPartial ? "text-[#FFA94D]" : "text-[#FF2D2D]"}`}>
            {isPaid ? "Paid in full" : isPartial ? "Partially paid" : "Balance due"}
          </div>
          <div className="font-display text-6xl mt-2 text-white tabular-nums" data-testid="public-balance">
            ${(isPaid ? total : balance).toFixed(2)}
          </div>
          {!isPaid && inv.due_date && (
            <div className="text-[11px] text-[#a3a3a3] mt-1 uppercase tracking-wider">Due {inv.due_date.slice(0,10)}</div>
          )}

          <div className="border-t border-[#262626] mt-5 pt-5 space-y-2" data-testid="public-line-items">
            {(inv.line_items || []).map((l) => (
              <div key={l.id} className="flex justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="text-white truncate">{l.description}</div>
                  <div className="text-[11px] text-[#a3a3a3]">
                    {l.quantity}× ${Number(l.unit_price).toFixed(2)}
                    {l.recurring && (
                      <span className="text-[#FF2D2D] ml-1.5">· recurring {l.recurring_interval}</span>
                    )}
                  </div>
                </div>
                <div className="text-sm tabular-nums shrink-0 text-white">${Number(l.line_total).toFixed(2)}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#262626] mt-5 pt-3 space-y-1 text-sm">
            <Row label="Subtotal" value={`$${Number(inv.subtotal_usd).toFixed(2)}`} />
            {Number(inv.tax_usd) > 0 && <Row label={`Tax (${Number(inv.tax_pct).toFixed(2)}%)`} value={`$${Number(inv.tax_usd).toFixed(2)}`} />}
            {paid > 0 && <Row label="Paid" value={`− $${paid.toFixed(2)}`} />}
            <Row label={isPaid ? "Total" : "Balance due"} value={`$${(isPaid ? total : balance).toFixed(2)}`} accent />
          </div>

          {inv.notes && (
            <div className="border-t border-[#262626] mt-5 pt-4 text-xs text-[#a3a3a3] whitespace-pre-wrap">{inv.notes}</div>
          )}
        </div>

        {!isPaid && (
          <>
            {inv.card_fee_pct > 0 && (
              <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-4 space-y-1 text-sm" data-testid="card-fee-breakdown">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-2">Card checkout total</div>
                <div className="flex items-center justify-between text-[#a3a3a3]">
                  <span>Invoice balance</span>
                  <span className="tabular-nums text-white">${balance.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-[#a3a3a3]">
                  <span>Card processing fee ({Number(inv.card_fee_pct).toFixed(1)}%)</span>
                  <span className="tabular-nums text-white">+ ${Number(inv.card_fee_usd || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#262626]">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Charged to card</span>
                  <span className="font-display text-xl tabular-nums text-white" data-testid="card-total">${Number(inv.card_total_usd || balance).toFixed(2)}</span>
                </div>
                <div className="text-[10px] text-[#666] mt-1">
                  Pay by cash / Zelle / CashApp to avoid the {Number(inv.card_fee_pct).toFixed(1)}% surcharge — contact us to arrange.
                </div>
              </div>
            )}
            <button onClick={pay} disabled={paying} data-testid="public-pay-btn"
              className="btn-primary w-full mt-4 px-6 py-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50">
              <CurrencyDollar size={16} weight="bold" />
              {paying ? "Redirecting to Stripe…" : `Pay $${Number(inv.card_total_usd || balance).toFixed(2)} securely with card`}
            </button>
            <div className="mt-3 text-[10px] uppercase tracking-wider text-[#666] flex items-center justify-center gap-1.5" data-testid="stripe-badge">
              <Lock size={10} weight="fill" /> Secure checkout by Stripe · No account required
            </div>
          </>
        )}

        {isPaid && (
          <div className="mt-6 border border-[#28C878]/40 bg-[#28C878]/5 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="public-paid-banner">
            <div className="flex items-center gap-2 text-[#28C878]">
              <CheckCircle size={18} weight="fill" />
              <div>
                <div className="text-sm font-bold">Payment received — thank you.</div>
                <div className="text-[11px] mt-0.5 text-[#a3a3a3]">A receipt has been emailed to {inv.client_email}.</div>
              </div>
            </div>
          </div>
        )}

        <a href={`${BACKEND}/api/public/invoices/${token}/pdf`} target="_blank" rel="noreferrer"
          data-testid="public-download-pdf"
          className="mt-4 text-xs uppercase tracking-wider text-[#a3a3a3] hover:text-[#FF2D2D] flex items-center justify-center gap-1.5">
          <Download size={11} /> Download invoice PDF
        </a>

        <div className="mt-10 text-center">
          <Link to="/" data-testid="public-back-home"
            className="text-[10px] uppercase tracking-[0.4em] text-[#666] hover:text-[#FF2D2D]">
            Vision Grafix →
          </Link>
        </div>
      </div>
    </Shell>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <div className={`text-[10px] uppercase tracking-[0.3em] ${accent ? "text-[#FF2D2D]" : "text-[#a3a3a3]"}`}>{label}</div>
      <div className={`tabular-nums ${accent ? "font-display text-xl text-white" : "text-[#a3a3a3] text-sm"}`}>{value}</div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#262626] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-[#FF2D2D] font-display text-xl">VISION</div>
            <div className="font-display text-xl">GRAFIX</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#666] hidden sm:block">Secure invoice payment</div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}
